import { CommonModule } from '@angular/common';
import { Component, EventEmitter, input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { SelectableField } from '@lx/quote-tool/lite/shared/types';
import { OptionCardComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { Course, DurationUnitEnum, PredefinedOnlineSelectionsWithCourses } from '@lx/shared/data-access/types';
import { PriceLabelComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { LocalizeAndPluralizePeriodPipe } from '@lx/shared/ui/pipes';
import { OnlineCoursePredefinedSelection } from './online-course-predefinition-selection.type';

/**
 * Presentational component for displaying and selecting online courses.
 *
 * This component displays a list of course options (merged with predefined selections)
 * and allows users to select a course. It emits the selected course and any predefined
 * selections (duration, start date) associated with that course.
 */
@Component({
  standalone: true,
  selector: 'lx-online-course-selector',
  templateUrl: './online-course-selector.component.html',
  styleUrls: ['./online-course-selector.component.scss'],
  imports: [CommonModule, OptionCardComponent, PriceLabelComponent, LocalizeAndPluralizePeriodPipe],
})
export class OnlineCourseSelectorComponent implements OnInit, OnChanges {
  /**
   * The merged course options to display (predefined selections merged with course data).
   * Parent component must fetch and merge the data before passing it here.
   */
  courseOptions = input<PredefinedOnlineSelectionsWithCourses[] | undefined>();

  initialSelectedCourse = input<CourseDetails | undefined>();
  currentDuration = input<number | undefined>();
  currentStartDate = input<string | undefined>();
  titlePathName = input<'name' | 'shortName'>('name');

  @Output() selectedCourseChanged = new EventEmitter<SelectableField<CourseDetails>>();
  @Output() predefinedSelectionsChanged = new EventEmitter<OnlineCoursePredefinedSelection>();

  label = $localize`Most popular`;
  selectedCourse: CourseDetails | undefined;

  protected readonly DurationUnitEnum = DurationUnitEnum;

  ngOnInit(): void {
    this.selectedCourse = this.initialSelectedCourse();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSelectedCourse']) {
      this.selectedCourse = this.initialSelectedCourse();
    }
  }

  setSelectedCourse(selectedCourse: PredefinedOnlineSelectionsWithCourses): void {
    this.selectedCourse = this.buildCourseDetails(selectedCourse) as CourseDetails;
    this.emitPredefinedOnlineSelections(selectedCourse);
    this.selectedCourseChanged.emit({ value: this.selectedCourse, fromUser: true });
  }

  private buildCourseDetails(selectedCourse: Course | CourseDetails): Partial<CourseDetails> {
    if (!selectedCourse) {
      return {};
    }

    const { code, name, durationMin, durationMax, courseDates, type } = selectedCourse;
    return {
      code,
      name,
      type: typeof type === 'string' ? type : type?.code,
      durationMin,
      durationMax,
      courseDates,
    };
  }

  private emitPredefinedOnlineSelections(selectedCourse: PredefinedOnlineSelectionsWithCourses): void {
    const predefinedSelection: OnlineCoursePredefinedSelection = {
      duration: !this.currentDuration() ? selectedCourse?.duration : undefined,
      startDate: !this.currentStartDate() ? selectedCourse?.startDate : undefined,
    };

    // Only emit if there are values to set
    if (predefinedSelection.duration !== undefined || predefinedSelection.startDate !== undefined) {
      this.predefinedSelectionsChanged.emit(predefinedSelection);
    }
  }
}
